/**
 * tenant-formatters — single source of truth for **all** localized
 * number/money/date formatting in the admin and dashboard.
 *
 * WHY THIS EXISTS
 * ───────────────
 * Before this module, ~45 call-sites in `src/app/admin/**` hardcoded
 * `'EUR'` and `'de-DE'` directly into `Intl.NumberFormat` and
 * `Intl.DateTimeFormat` constructors. A US tenant on `currency='USD'`,
 * `default_locale='en'` would still see "€" prefixes and German date
 * order in their dashboard. For the global-expansion phase we need
 * every formatter to read its currency/locale/timezone from the
 * **tenant**, not from a literal string.
 *
 * ARCHITECTURE
 * ────────────
 * Two layers:
 *   1. Pure formatters (this file, top-half) — work server-side AND
 *      client-side, take `{currency, locale, timezone}` explicitly.
 *   2. React hook `useAdminFormatters()` (`src/hooks/useAdminFormatters.ts`)
 *      — pulls those three from the `useActiveTenant` context and
 *      curries the pure formatters so call-sites read like
 *      `fmt.money(amount)` instead of `formatMoney(amount, ctx)`.
 *
 * CURRENCY-OF-RECORD
 * ──────────────────
 * `tenant.currency` is the **display** currency for aggregates (dashboard
 * stats, totals). For per-row amounts (invoice, booking) that already
 * carry their own currency column (e.g. `tenant_invoices.currency`,
 * `tenant_subscriptions.currency`), pass that row's currency to the
 * formatter explicitly — do NOT fall back to `tenant.currency`. The
 * row currency is locked at create-time and surviving a tenant-level
 * change is the whole point.
 *
 * VERCEL POLICY
 * ─────────────
 * Date formatters use `date-fns-tz` exclusively. `Intl.DateTimeFormat`
 * with a `timeZone` option is banned in this codebase by ESLint (see
 * `docs/VERCEL_TIMEZONE.md`) because it drifts between Vercel's UTC
 * Node runtime and local dev. Number/currency formatting via
 * `Intl.NumberFormat` is fine — no timezone involvement there.
 */
import { formatInTimeZone } from 'date-fns-tz';
import { dateFnsLocaleFor, type TenantTimezone } from './tenant-time';

declare const currencyCodeBrand: unique symbol;

/**
 * ISO 4217 currency code (3 uppercase letters). Branded to prevent
 * raw `string` values from sneaking into formatter call-sites — only
 * `assertCurrencyCode(s)` / DB reads produce a value of this type.
 */
export type CurrencyCode = string & { readonly [currencyCodeBrand]: true };

/**
 * Validate + brand a string as an ISO 4217 currency. Throws on
 * malformed input — callers should treat that as a programmer error
 * (config drift, bad DB row). Use `tryParseCurrencyCode` for inputs
 * that may legitimately be invalid (e.g. user form input).
 */
export function assertCurrencyCode(s: string): CurrencyCode {
  if (!/^[A-Z]{3}$/.test(s)) {
    throw new Error(`Invalid ISO 4217 currency code: ${s}`);
  }
  return s as CurrencyCode;
}

export function tryParseCurrencyCode(s: string | null | undefined): CurrencyCode | null {
  if (!s || !/^[A-Z]{3}$/.test(s)) return null;
  return s as CurrencyCode;
}

/** Loose locale string (BCP 47, e.g. 'de', 'de-DE', 'en-US'). */
export type LocaleTag = string;

/**
 * The three values that drive every tenant-aware formatter. Read off
 * a tenant row OR the ActiveTenant context. `useAdminFormatters` does
 * exactly this lookup so call-sites only see `{ money, date, … }`.
 */
export interface TenantFormatContext {
  currency: CurrencyCode;
  locale: LocaleTag;
  timezone: TenantTimezone;
}

const DEFAULT_CURRENCY: CurrencyCode = 'EUR' as CurrencyCode;
const DEFAULT_LOCALE: LocaleTag = 'de';
const DEFAULT_TIMEZONE: TenantTimezone = 'Europe/Berlin';

/**
 * Resolve a `TenantFormatContext` from any shape that carries the
 * three fields — a fully-loaded `tenants` row, the ActiveTenant
 * context, or an explicit override. Each field falls back to the
 * platform default ONLY when missing; never silently substitutes a
 * different tenant's value.
 */
export function resolveFormatContext(input: {
  currency?: string | null;
  default_locale?: string | null;
  locale?: string | null;
  timezone?: string | null;
}): TenantFormatContext {
  const currency =
    tryParseCurrencyCode((input.currency ?? '').toUpperCase()) ?? DEFAULT_CURRENCY;
  const locale = input.locale || input.default_locale || DEFAULT_LOCALE;
  const timezone = (input.timezone || DEFAULT_TIMEZONE) as TenantTimezone;
  return { currency, locale, timezone };
}

// ─── Money / number formatters ─────────────────────────────────────

export interface FormatMoneyOptions {
  /** Override the context currency for a single call — e.g. when
   *  formatting a row that has its own `row.currency` distinct from
   *  the tenant's display currency. */
  currency?: CurrencyCode | string;
  /** Override the context locale for a single call. */
  locale?: LocaleTag;
  /** Default `2`. Set to `0` for whole-number revenue displays. */
  minimumFractionDigits?: number;
  /** Default `2`. */
  maximumFractionDigits?: number;
}

/**
 * Format a money amount in the tenant's display currency.
 *
 * @param amount  Decimal currency units (e.g. `12.50` for €12.50).
 *                For Stripe-style integer-cents inputs use
 *                `formatMoneyCents(cents, ...)` instead.
 */
export function formatMoney(
  amount: number,
  ctx: TenantFormatContext,
  options: FormatMoneyOptions = {},
): string {
  const currency = options.currency ?? ctx.currency;
  const locale = options.locale ?? ctx.locale;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: String(currency).toUpperCase(),
    minimumFractionDigits: options.minimumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(amount);
}

/**
 * Format Stripe-style integer cents as a money string. Divide-by-100
 * happens **once**, in this helper, so call-sites never accumulate
 * floating-point rounding error across the codebase.
 */
export function formatMoneyCents(
  cents: number,
  ctx: TenantFormatContext,
  options: FormatMoneyOptions = {},
): string {
  // Round to integer cents first to defend against any non-integer
  // input slipping in from an upstream `Number(...)` parse.
  const wholeCents = Math.round(cents);
  return formatMoney(wholeCents / 100, ctx, options);
}

/** Format a plain number using the tenant's locale conventions. */
export function formatNumber(
  n: number,
  ctx: TenantFormatContext,
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(ctx.locale, options).format(n);
}

/** `0.123` → "12,3 %" in DE, "12.3%" in EN. */
export function formatPercent(
  n: number,
  ctx: TenantFormatContext,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  return new Intl.NumberFormat(ctx.locale, {
    style: 'percent',
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 1,
  }).format(n);
}

// ─── Date / time formatters — date-fns-tz only ─────────────────────

/**
 * Format a date in the tenant's timezone + locale. Pattern defaults
 * to date-fns `'P'` (short date — e.g. "26.05.2026" in DE,
 * "5/26/2026" in EN). The 12h vs 24h convention is automatically
 * derived from the locale (e.g. 'p' = "14:30" in DE, "2:30 PM" in EN).
 *
 * @param date     Date instant or ISO string.
 * @param pattern  date-fns token string. Default `'P'`.
 */
export function formatDate(
  date: Date | number | string,
  ctx: TenantFormatContext,
  pattern: string = 'P',
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return formatInTimeZone(d, ctx.timezone, pattern, {
    locale: dateFnsLocaleFor(ctx.locale),
  });
}

/** Short date — "26.05.2026" / "5/26/2026". */
export function formatShortDate(
  date: Date | number | string,
  ctx: TenantFormatContext,
): string {
  return formatDate(date, ctx, 'P');
}

/** Long date with weekday — "Mittwoch, 26. Mai 2026". */
export function formatLongDate(
  date: Date | number | string,
  ctx: TenantFormatContext,
): string {
  return formatDate(date, ctx, 'PPPP');
}

/** Time only — "14:30" (DE) / "2:30 PM" (EN). Locale drives 12h/24h. */
export function formatTime(
  date: Date | number | string,
  ctx: TenantFormatContext,
): string {
  return formatDate(date, ctx, 'p');
}

/** Date + time combined — "26.05.2026, 14:30" / "5/26/2026, 2:30 PM". */
export function formatDateTime(
  date: Date | number | string,
  ctx: TenantFormatContext,
): string {
  return formatDate(date, ctx, 'Pp');
}

// ─── Duration ──────────────────────────────────────────────────────

/**
 * Render a minute-count as a human duration. Locale-agnostic on
 * purpose — the unit suffix uses the locale, but the layout is the
 * same everywhere.
 */
export function formatDuration(
  minutes: number,
  ctx: TenantFormatContext,
): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const hourSuffix = ctx.locale.startsWith('de') ? 'Std' : 'h';
  const minSuffix = ctx.locale.startsWith('de') ? 'Min' : 'min';
  if (hours === 0) return `${mins} ${minSuffix}`;
  if (mins === 0) return `${hours} ${hourSuffix}`;
  return `${hours} ${hourSuffix} ${mins} ${minSuffix}`;
}
